import React, { memo, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { getCurrencyCase, getCurrencyCaseByValue } from 'utils';
import { useSelectorProps } from 'hooks';
import {
  getMonthlyNormalizedExchangeRateDynamicsData,
  getYearlyNormalizedExchangeRateDynamicsData,
  getLocationParamsValuesForHooks,
  getCurrenciesPairInfoForHooks,
} from 'selectors';

import ContentSection from 'components/ContentSection';
import DynamicRatesChart from 'components/ConvertorLandingDynamicRatesChart';
import RatesTable from 'components/RatesTable';

import { SectionTitle } from 'components/SectionTitle';

import { Text, Line } from './styles';

const getPreparedValue = value => Math.round(Math.abs(value));

const RatesDynamic = memo(() => {
  const { formatNumber } = useIntl();

  const selectorProps = useSelectorProps();

  const { source, target } = useSelector(state =>
    getCurrenciesPairInfoForHooks(state, selectorProps)
  );

  const { sourceValue } = useSelector(state =>
    getLocationParamsValuesForHooks(state, selectorProps)
  );

  const monthlyRates = useSelector(state =>
    getMonthlyNormalizedExchangeRateDynamicsData(state, selectorProps)
  );
  const yearlyRates = useSelector(state =>
    getYearlyNormalizedExchangeRateDynamicsData(state, selectorProps)
  );

  const monthlyDiff = useMemo(
    () => monthlyRates[0]?.summ - monthlyRates[monthlyRates.length - 1]?.summ,
    [monthlyRates]
  );

  const yearlyDiff = useMemo(
    () => yearlyRates[0]?.summ - yearlyRates[yearlyRates.length - 1]?.summ,
    [yearlyRates]
  );

  const formattedSourceValue = formatNumber(sourceValue);

  const sourceCase =
    sourceValue === '1'
      ? getCurrencyCase(source, target, 'g').currencyGS
      : getCurrencyCase(source, target, 'gp').currencyGpS;

  const targetCase = getCurrencyCase(source, target, 'mnps').currencyMnpsT;

  const monthMessageValues = useMemo(
    () => ({
      sourceValue: formattedSourceValue,
      diff: Number.isNaN(monthlyDiff)
        ? '-'
        : formatNumber(getPreparedValue(monthlyDiff)),
      diffCase: getCurrencyCaseByValue(source, target, monthlyDiff, 'acc')
        .currencyValueCaseAccT,
      sourceCase,
      targetCase,
    }),
    [
      source,
      target,
      sourceValue,
      monthlyDiff,
      sourceCase,
      targetCase,
      formattedSourceValue,
    ]
  );

  const yearMessageValues = useMemo(
    () => ({
      sourceValue: formattedSourceValue,
      diff: Number.isNaN(yearlyDiff)
        ? '-'
        : formatNumber(getPreparedValue(yearlyDiff)),
      diffCase: getCurrencyCaseByValue(source, target, yearlyDiff, 'acc')
        .currencyValueCaseAccT,
      sourceCase,
      targetCase,
    }),
    [
      source,
      target,
      sourceValue,
      yearlyDiff,
      sourceCase,
      targetCase,
      formattedSourceValue,
    ]
  );

  return (
    <ContentSection as="section">
      <SectionTitle>
        <FormattedMessage id="dynamicRates.title" />
      </SectionTitle>

      <DynamicRatesChart />

      {monthlyRates && monthlyRates.length > 0 && (
        <>
          <Line />

          <SectionTitle>
            <FormattedMessage id="month.change.title" />
          </SectionTitle>

          <Text>
            <FormattedMessage
              id={
                monthlyDiff < 0 ? 'month.decrease.text' : 'month.increase.text'
              }
              values={monthMessageValues}
            />
          </Text>

          <RatesTable rates={monthlyRates} />
        </>
      )}

      {yearlyRates && yearlyRates.length > 0 && (
        <>
          <Line />
          <SectionTitle>
            <FormattedMessage id="year.change.title" />
          </SectionTitle>

          <Text>
            <FormattedMessage
              id={yearlyDiff < 0 ? 'year.decrease.text' : 'year.increase.text'}
              values={yearMessageValues}
            />
          </Text>

          <RatesTable rates={yearlyRates} />
        </>
      )}
    </ContentSection>
  );
});

export default RatesDynamic;
